import { mediaHttp } from '@/lib/api/http-client';

export const TEMPLATE_FILE_NAME = 'monksflow-media-plan.zip';

/**
 * Fetches the file a job's plan was read from, as it was uploaded.
 *
 * Bearer-guarded like the template, so it is a request rather than a plain link.
 */
export async function fetchJobSource(jobId: string, signal?: AbortSignal): Promise<Blob> {
  const res = await mediaHttp.get({
    responseType: 'blob',
    security: [{ scheme: 'bearer', type: 'http' }],
    url: `/api/v1/delivery-jobs/${jobId}/source`,
    signal,
  });

  return res.data as Blob;
}

/**
 * Fetches the media plan template: the workbook and the creative images its worked example
 * names, so the example rows can be seen alongside the pictures they refer to. Unpacked
 * locally rather than sent back as one archive - the sheet and the creatives upload on
 * separate routes.
 *
 * A real request rather than a plain `<a href>`: the endpoint is bearer-guarded,
 * so the browser's own navigation would arrive unauthenticated.
 */
export async function fetchMediaPlanTemplate(signal?: AbortSignal): Promise<Blob> {
  const res = await mediaHttp.get({
    responseType: 'blob',
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/api/v1/media-plan-templates',
    signal,
  });

  return res.data as Blob;
}

/**
 * Hands the workbook to the browser's downloader.
 *
 * The object URL is revoked on the next tick — revoking it synchronously can
 * cancel the download before Chrome has read the blob.
 */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  // The repo lints `createElement` as deprecated in favour of React, but handing
  // a file to the browser's downloader has to happen outside the render tree.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
